import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import IfIcon from '@/components/atoms/Icons/IfIcon';
import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
  PageSubHeaderDeskDropdown,
} from '@/components/organisms/PageSubHeader';
import { DeskDropdown } from '@/components/organisms/PageSubHeader/DeskDropdown';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { useDeskId } from '@/hooks/useDeskId';
import { NEW_RULE_ID } from '@/modules/rules/constants';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

type RulesHeaderProps = { isEmpty?: boolean };

const RulesHeader = ({ isEmpty = false }: RulesHeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const deskId = useDeskId();
  const slug = useSelector(selectAccountSlug);
  const canWrite = useSelector((state: RootState) => getPermissions(state, 'rules', actions.WRITE));

  return (
    <PageSubHeader
      breadcrumbs={
        <PageSubHeaderBreadcrumbs>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>
                  <div className="flex items-center">
                    <div className="flex size-8 items-center justify-center">
                      <IfIcon size={16} />
                    </div>
                    <div className="mr-4 ml-2 h-4 w-px bg-border" />
                    <span className="text-sm leading-5 font-normal">{t('rules.rules')}</span>
                  </div>
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </PageSubHeaderBreadcrumbs>
      }
      actions={
        !isEmpty && (
          <PageSubHeaderActions>
            <Button
              size="sm"
              disabled={!deskId || !canWrite}
              onClick={() => navigate(`/${slug}/environments/${deskId}/rules/${NEW_RULE_ID}`)}
            >
              <PlusIcon size={16} />
              {t('common.create')}
            </Button>
          </PageSubHeaderActions>
        )
      }
      deskDropdown={
        <PageSubHeaderDeskDropdown>
          <DeskDropdown getPath={(id) => `/${slug}/environments/${id}/rules`} />
        </PageSubHeaderDeskDropdown>
      }
    />
  );
};

export default RulesHeader;
